import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import configService from "../../../services/configService";
import {
  fetchAcademiesBySport,
  fetchCentersByAcademy,
  fetchCoursesByCenter,
  fetchAgeCategoriesByCourse,
  fetchAvailableTrialSlots,
  fetchTrialSlotsForDate,
  addTrial,
  type TrialAcademy,
  type TrialCenter,
  type TrialCourse,
  type TrialSlot,
} from "../../../api/academyEnrolments/trialsService";

export interface SportOption {
  value: string;
  label: string;
}

export interface AcademyOption {
  value: string;
  label: string;
  academy: TrialAcademy;
}

export interface CenterOption {
  value: string;
  label: string;
  center: TrialCenter;
}

export interface CourseOption {
  value: string;
  label: string;
  course: TrialCourse;
}

export interface AgeCategoryOption {
  value: string;
  label: string;
}

export interface SlotOption {
  value: string;
  label: string;
  slot: TrialSlot;
}

export interface RelationshipOption {
  value: string;
  label: string;
}

export const RELATIONSHIP_OPTIONS: RelationshipOption[] = [
  { value: "Mother", label: "Mother" },
  { value: "Father", label: "Father" },
  { value: "Guardian", label: "Guardian" },
  { value: "Grandparent", label: "Grandparent" },
  { value: "Uncle", label: "Uncle" },
  { value: "Aunt", label: "Aunt" },
];

export interface TrialFormState {
  playerName: string;
  mobile: string;
  enrolledBy: "self" | "parent";
  parentName: string;
  relationship: RelationshipOption | null;
  sport: SportOption | null;
  academy: AcademyOption | null;
  center: CenterOption | null;
  course: CourseOption | null;
  ageCategory: AgeCategoryOption | null;
  date: Date | null;
  slot: SlotOption | null;
}

export interface TrialFormLoading {
  academies: boolean;
  centers: boolean;
  courses: boolean;
  ageCategories: boolean;
  dates: boolean;
  slots: boolean;
  submitting: boolean;
}

const initialState: TrialFormState = {
  playerName: "",
  mobile: "",
  enrolledBy: "self",
  parentName: "",
  relationship: null,
  sport: null,
  academy: null,
  center: null,
  course: null,
  ageCategory: null,
  date: null,
  slot: null,
};

export function useTrialForm() {
  const navigate = useNavigate();

  const [form, setForm] = useState<TrialFormState>(initialState);
  const [loading, setLoading] = useState<TrialFormLoading>({
    academies: false,
    centers: false,
    courses: false,
    ageCategories: false,
    dates: false,
    slots: false,
    submitting: false,
  });

  const [sportOptions, setSportOptions] = useState<SportOption[]>([]);
  const [academyOptions, setAcademyOptions] = useState<AcademyOption[]>([]);
  const [centerOptions, setCenterOptions] = useState<CenterOption[]>([]);
  const [courseOptions, setCourseOptions] = useState<CourseOption[]>([]);
  const [ageCategoryOptions, setAgeCategoryOptions] = useState<AgeCategoryOption[]>([]);
  const [availableDates, setAvailableDates] = useState<string[]>([]);
  const [slotOptions, setSlotOptions] = useState<SlotOption[]>([]);
  const [errors, setErrors] = useState<Partial<Record<keyof TrialFormState, string>>>({});

  // Load sports from config on mount
  useEffect(() => {
    configService.getConfig().then((config) => {
      const options: SportOption[] = config.sports
        .filter((s) => s.isActive)
        .map((s) => ({ value: s.name, label: s.name }));
      setSportOptions(options);
    });
  }, []);

  // Fetch academies when sport changes
  useEffect(() => {
    if (!form.sport) {
      setAcademyOptions([]);
      return;
    }

    setLoading((prev) => ({ ...prev, academies: true }));
    setAcademyOptions([]);

    fetchAcademiesBySport(form.sport.value.replace(/\s+/g, ""))
      .then((res) => {
        const options: AcademyOption[] = (res.data ?? []).map((a) => ({
          value: a._id,
          label: a.basicDetails.academyName,
          academy: a,
        }));
        setAcademyOptions(options);
      })
      .catch(() => toast.error("Failed to load academies. Please try again."))
      .finally(() => setLoading((prev) => ({ ...prev, academies: false })));
  }, [form.sport]);

  // Fetch centers when academy changes
  useEffect(() => {
    if (!form.academy) {
      setCenterOptions([]);
      return;
    }

    setLoading((prev) => ({ ...prev, centers: true }));
    setCenterOptions([]);

    fetchCentersByAcademy(form.academy.academy.slug)
      .then((res) => {
        const results = res.data?.paginatedResults ?? [];
        const options: CenterOption[] = results.map((c) => ({
          value: c._id,
          label: c.basicDetails.location,
          center: c,
        }));
        setCenterOptions(options);
      })
      .catch(() => toast.error("Failed to load centers. Please try again."))
      .finally(() => setLoading((prev) => ({ ...prev, centers: false })));
  }, [form.academy]);

  // Fetch courses when center changes
  useEffect(() => {
    if (!form.center) {
      setCourseOptions([]);
      return;
    }

    setLoading((prev) => ({ ...prev, courses: true }));
    setCourseOptions([]);

    fetchCoursesByCenter(form.center.value)
      .then((res) => {
        const options: CourseOption[] = (res.data ?? []).map((c) => ({
          value: c._id,
          label: `${c.courseName} (${c.proficiency})`,
          course: c,
        }));
        setCourseOptions(options);
      })
      .catch(() => toast.error("Failed to load courses. Please try again."))
      .finally(() => setLoading((prev) => ({ ...prev, courses: false })));
  }, [form.center]);

  // Fetch age categories when course changes
  useEffect(() => {
    if (!form.center || !form.course) {
      setAgeCategoryOptions([]);
      return;
    }

    setLoading((prev) => ({ ...prev, ageCategories: true }));
    setAgeCategoryOptions([]);

    fetchAgeCategoriesByCourse(form.center.value, form.course.value)
      .then((res) => {
        const options: AgeCategoryOption[] = (res.data ?? []).map((s) => ({
          value: s,
          label: s,
        }));
        setAgeCategoryOptions(options);
      })
      .catch(() => toast.error("Failed to load age categories. Please try again."))
      .finally(() => setLoading((prev) => ({ ...prev, ageCategories: false })));
  }, [form.center, form.course]);

  // Fetch available dates when age category changes
  useEffect(() => {
    if (!form.center || !form.course || !form.ageCategory) {
      setAvailableDates([]);
      return;
    }

    setLoading((prev) => ({ ...prev, dates: true }));
    setAvailableDates([]);

    fetchAvailableTrialSlots(form.center.value, form.course.value, form.ageCategory.value)
      .then((res) => {
        const batches = res.data ?? [];
        const dateSet = new Set<string>();
        for (const batch of batches) {
          for (const slot of batch.availableSlots ?? []) {
            if (slot.date) dateSet.add(slot.date);
          }
        }
        setAvailableDates(Array.from(dateSet).sort());
      })
      .catch(() => toast.error("Failed to load available dates. Please try again."))
      .finally(() => setLoading((prev) => ({ ...prev, dates: false })));
  }, [form.center, form.course, form.ageCategory]);

  // Fetch slots when date changes
  useEffect(() => {
    if (!form.center || !form.course || !form.ageCategory || !form.date) {
      setSlotOptions([]);
      return;
    }

    const dateStr = formatDateToYMD(form.date);
    setLoading((prev) => ({ ...prev, slots: true }));
    setSlotOptions([]);

    fetchTrialSlotsForDate(
      form.course.value,
      form.center.value,
      form.ageCategory.value,
      dateStr
    )
      .then((res) => {
        const options: SlotOption[] = (res.data?.slots ?? []).map((s) => ({
          value: `${s.startTime}-${s.endTime}`,
          label: `${s.startTime} - ${s.endTime}`,
          slot: s,
        }));
        setSlotOptions(options);
      })
      .catch(() => toast.error("Failed to load time slots. Please try again."))
      .finally(() => setLoading((prev) => ({ ...prev, slots: false })));
  }, [form.center, form.course, form.ageCategory, form.date]);

  const setField = useCallback(
    <K extends keyof TrialFormState>(field: K, value: TrialFormState[K]) => {
      setForm((prev) => {
        const next = { ...prev, [field]: value };

        // Cascade resets downstream
        if (field === "sport") {
          next.academy = null;
          next.center = null;
          next.course = null;
          next.ageCategory = null;
          next.date = null;
          next.slot = null;
        }
        if (field === "academy") {
          next.center = null;
          next.course = null;
          next.ageCategory = null;
          next.date = null;
          next.slot = null;
        }
        if (field === "center") {
          next.course = null;
          next.ageCategory = null;
          next.date = null;
          next.slot = null;
        }
        if (field === "course") {
          next.ageCategory = null;
          next.date = null;
          next.slot = null;
        }
        if (field === "ageCategory") {
          next.date = null;
          next.slot = null;
        }
        if (field === "date") {
          next.slot = null;
        }

        return next;
      });

      // Clear error on field change
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    },
    []
  );

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TrialFormState, string>> = {};

    if (!form.playerName.trim()) newErrors.playerName = "Player name is required.";
    if (!/^\d{10}$/.test(form.mobile)) newErrors.mobile = "Enter a valid 10-digit mobile number.";

    if (form.enrolledBy === "parent") {
      if (!form.parentName.trim()) newErrors.parentName = "Parent name is required.";
      if (!form.relationship) newErrors.relationship = "Relationship is required.";
    }

    if (!form.sport) newErrors.sport = "Please select a sport.";
    if (!form.academy) newErrors.academy = "Please select an academy.";
    if (!form.center) newErrors.center = "Please select a center.";
    if (!form.course) newErrors.course = "Please select a course.";
    if (!form.ageCategory) newErrors.ageCategory = "Please select an age category.";
    if (!form.date) newErrors.date = "Please select a trial date.";
    if (!form.slot) newErrors.slot = "Please select a time slot.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = (): boolean => {
    if (!form.playerName.trim() || !/^\d{10}$/.test(form.mobile)) return false;
    if (form.enrolledBy === "parent" && (!form.parentName.trim() || !form.relationship)) return false;
    if (!form.sport || !form.academy || !form.center || !form.course || !form.ageCategory || !form.date || !form.slot) return false;
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!form.sport || !form.academy || !form.center || !form.course || !form.ageCategory || !form.date || !form.slot) return;

    setLoading((prev) => ({ ...prev, submitting: true }));

    try {
      const payload = {
        fullName: form.playerName.trim(),
        countryCode: 91,
        phoneNumber: Number(form.mobile),
        academy: form.center.center.academyInfo._id,
        coachingCenter: form.center.value,
        bookingType: "free-trial" as const,
        sports: form.sport.value,
        course: form.course.value,
        ageCategory: form.ageCategory.value,
        date: formatDateToYMD(form.date),
        startTime: form.slot.slot.startTime,
        endTime: form.slot.slot.endTime,
        location: form.center.center.basicDetails.location,
        registeredThrough: "web" as const,
        source: "admin" as const,
        enrolledBy: form.enrolledBy,
        ...(form.enrolledBy === "parent" && {
          parentName: form.parentName.trim(),
          relationship: form.relationship?.value,
        }),
      };

      await addTrial(payload);
      return true;
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to add trial. Please try again.";
      toast.error(msg);
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, submitting: false }));
    }
  };

  const reset = () => {
    setForm(initialState);
    setErrors({});
    setAcademyOptions([]);
    setCenterOptions([]);
    setCourseOptions([]);
    setAgeCategoryOptions([]);
    setAvailableDates([]);
    setSlotOptions([]);
  };

  return {
    form,
    loading,
    errors,
    sportOptions,
    academyOptions,
    centerOptions,
    courseOptions,
    ageCategoryOptions,
    availableDates,
    slotOptions,
    setField,
    isFormValid,
    handleSubmit,
    reset,
    navigate,
  };
}

function formatDateToYMD(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
